import CreditSVG from "@/../../hypetrain/static/img/credit.svg";
import { useQuery } from "@apollo/client";
import { Callout, Classes, Portal } from "@blueprintjs/core";
import styled from "@emotion/styled";

import { formatMoney } from "@/components/helpers/string-utils";
import EmbedSvg from "@/components/helpers/ui/EmbedSvg";
import { isInWorkspace } from "@/components/helpers/workspace/permissions";
import { Box, Flex } from "@/components/layout/flexbox";
import BackDrop from "@/components/pieces/BackDrop";
import DialogHeader from "@/components/pieces/dialog/DialogHeader";
import ScrollableModal from "@/components/pieces/ScrollableModal";
import {
  GREEN,
  HEADING_FONT,
  MIDTEXT_COLOR,
  PORTAL_Z_INDEX,
} from "@/css/constants";
import { CreditPackagesDocument } from "@/graphql";

export const Subheading = styled("h2")`
  font-size: 24px;
  font-family: ${HEADING_FONT};
  margin: 0px;
`;

const NUM_PRODUCTS = 6;
const FEATURED_SLUGS = [
  "1-credit",
  "2-credits",
  "4-credits",
  "8-credits",
  "12-credits",
  "25-credits",
  "50-credits",
  "100-credits",
  "250-credits",
  "500-credits",
];

export default function CreditExplainerModal({ minCredits, visible, onClose }) {
  const site =
    typeof window !== "undefined" &&
    !!localStorage.getItem("forcePlacementPricing")
      ? "Placement"
      : undefined;
  const { data } = useQuery(CreditPackagesDocument, {
    variables: {
      site,
    },
  });
  const inWorkspace = isInWorkspace();
  return (
    <Portal>
      <BackDrop visible={visible} zIndex={PORTAL_Z_INDEX} opacity={0.6} />
      <ScrollableModal
        visible={visible}
        onClose={onClose}
        verticallyCenter={true}
        canOutsideClickClose={false}
        style={{
          position: "absolute",
          top: 24,
          left: 0,
          width: "100%",
          overflowY: "auto",
          boxSizing: "border-box",
        }}
      >
        <Box
          backgroundColor="#FFF"
          className="round-corners"
          width={550}
          maxWidth="100%"
          margin="0 auto"
        >
          <DialogHeader onClose={onClose}>
            <Subheading>
              {inWorkspace
                ? "Stretch your budget further with credits"
                : "Own your development with credits"}
            </Subheading>
          </DialogHeader>
          <Box p={24}>
            <Box>
              {inWorkspace ? (
                <>
                  Credits let you flexibly allocate budget to the people and
                  programs of your choosing. Re-allocate credits whenever you
                  like for up to a year after purchase.
                </>
              ) : (
                <>
                  Credits give you the freedom to book any service or coach on
                  the Exec platform. Use them at your convenience for up to a
                  year after purchase.
                </>
              )}
            </Box>
            <Box mt={3} fontSize={14}>
              <Callout intent="primary">
                {inWorkspace ? (
                  <>
                    Unlock preferred pricing when you commit to a larger package
                    of credits.
                  </>
                ) : (
                  <>
                    Unlock preferred pricing by committing to your development
                    in advance.
                  </>
                )}
              </Callout>
            </Box>
            <Box mt={3}>
              {!data ? (
                <Box className={Classes.SKELETON} height={215} />
              ) : (
                <Box className="bas round-corners">
                  <CreditPackages
                    minCredits={minCredits}
                    products={data.creditPackages}
                  />
                </Box>
              )}
            </Box>
            <Box mt={3} fontWeight={600}>
              To buy credits, simply add extra credits to your order during
              booking.
            </Box>
          </Box>
        </Box>
      </ScrollableModal>
    </Portal>
  );
}

function CreditPackages({ minCredits, products }) {
  // Filter out products that don't cover the order total.
  // Also filter out non-featured products
  // unless they exactly match the minCredits amount
  const displayProducts = products
    .filter(
      (p) =>
        p.creditValue >= minCredits &&
        (p.creditValue == minCredits || FEATURED_SLUGS.includes(p.slug)),
    )
    .sort((x, y) => (x.creditValue > y.creditValue ? 1 : -1))
    .slice(0, NUM_PRODUCTS);
  return (
    <Box className="bas round-corners">
      {displayProducts.map((product, i) => (
        <Flex
          key={product.id}
          p={3}
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          className={i + 1 < displayProducts.length ? "bbs" : ""}
        >
          <Flex fontWeight={500} color={MIDTEXT_COLOR}>
            <span style={{ marginRight: 4 }}>
              <EmbedSvg src={CreditSVG} height={18} width={18} />
            </span>
            {product.name}
          </Flex>
          <Flex alignItems="center">
            {product.msrp ? (
              <Box
                fontSize={18}
                fontWeight={500}
                color={"rgba(119, 131, 151, 0.6)"}
                style={{ textDecoration: "line-through" }}
              >
                {formatMoney(product.msrp, 0)}
              </Box>
            ) : null}
            {product.price ? (
              <Box
                ml={product.msrp ? 2 : null}
                fontWeight={500}
                color={product.msrp ? GREEN : MIDTEXT_COLOR}
              >
                {formatMoney(product.price, 0)}
              </Box>
            ) : null}
          </Flex>
        </Flex>
      ))}
    </Box>
  );
}
